"use client";

import React from "react";

import { Checkbox } from "./Checkbox";

export interface ColorOption {
  id: string;
  label: string;
  hex: string;
}

export interface ColorFamilyFilterProps {
  colors: readonly ColorOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  counts?: Map<string, number>;
  label?: string;
  className?: string;
}

export const DEFAULT_COLOR_OPTIONS = [
  { id: "Pink", label: "Pink", hex: "#EC4899" },
  { id: "Red", label: "Red", hex: "#DC2626" },
  { id: "Orange", label: "Orange", hex: "#F97316" },
  { id: "Brown", label: "Brown", hex: "#92400E" },
  { id: "Yellow", label: "Yellow", hex: "#EAB308" },
  { id: "Green", label: "Green", hex: "#16A34A" },
  { id: "Blue", label: "Blue", hex: "#2563EB" },
  { id: "Purple", label: "Purple", hex: "#9333EA" },
  { id: "White", label: "White", hex: "#FFFFFF" },
  { id: "Gray", label: "Gray", hex: "#6B7280" },
  { id: "Black", label: "Black", hex: "#000000" },
] as const satisfies readonly ColorOption[];

const DARK_TEXT_COLOR = "#111827";
const LIGHT_TEXT_COLOR = "#FFFFFF";

function hexChannelToLinear(channel: number): number {
  const value = channel / 255;
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function parseHexColor(hex: string): [number, number, number] | null {
  const normalized = hex.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;

  return [
    Number.parseInt(normalized.slice(0, 2), 16),
    Number.parseInt(normalized.slice(2, 4), 16),
    Number.parseInt(normalized.slice(4, 6), 16),
  ];
}

function getRelativeLuminance(hex: string): number | null {
  const channels = parseHexColor(hex);
  if (!channels) return null;

  const [red, green, blue] = channels.map(hexChannelToLinear);
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function getContrastRatio(firstLuminance: number, secondLuminance: number): number {
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);
  return (lighter + 0.05) / (darker + 0.05);
}

export function getColorFamilyFilterTextColor(backgroundHex: string): string {
  const backgroundLuminance = getRelativeLuminance(backgroundHex);
  if (backgroundLuminance === null) return DARK_TEXT_COLOR;

  const darkTextLuminance = getRelativeLuminance(DARK_TEXT_COLOR) ?? 0;
  const lightTextLuminance = getRelativeLuminance(LIGHT_TEXT_COLOR) ?? 1;
  const darkContrast = getContrastRatio(backgroundLuminance, darkTextLuminance);
  const lightContrast = getContrastRatio(backgroundLuminance, lightTextLuminance);

  return darkContrast >= lightContrast ? DARK_TEXT_COLOR : LIGHT_TEXT_COLOR;
}

function ChevronDown({ isExpanded }: { isExpanded: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      style={{ transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)" }}
    >
      <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function ColorFamilyFilter({
  colors,
  selected,
  onChange,
  counts,
  label = "COLOR FAMILY",
  className = "",
}: ColorFamilyFilterProps) {
  const [isExpanded, setIsExpanded] = React.useState(true);

  const toggleColor = (colorId: string) => {
    const count = counts?.get(colorId) ?? 0;
    if (counts && count <= 0 && !selected.includes(colorId)) {
      return;
    }

    const nextSelected = selected.includes(colorId)
      ? selected.filter((id) => id !== colorId)
      : [...selected, colorId];
    onChange(nextSelected);
  };

  return (
    <div className={className}>
      <div className="flex h-[41.974px] w-full items-center justify-between">
        <span className="text-[10.5px] font-medium uppercase leading-[14px] tracking-[0.6173px] text-muted-foreground">
          {label}
        </span>
        <button
          onClick={() => setIsExpanded((prev) => !prev)}
          style={{
            width: "14px",
            height: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: 0,
          }}
        >
          <ChevronDown isExpanded={isExpanded} />
        </button>
      </div>

      {isExpanded && (
        <div className="mt-[21.983px] flex flex-col">
          {colors.map((color, index) => {
            const isSelected = selected.includes(color.id);
            const count = counts?.get(color.id) ?? 0;
            const isDisabled = Boolean(counts && count <= 0 && !isSelected);
            const textColor = getColorFamilyFilterTextColor(color.hex);
            return (
              <div
                key={color.id}
                onClick={() => toggleColor(color.id)}
                className={`flex min-h-[30px] w-full items-center ${
                  isDisabled ? "cursor-default opacity-40" : "cursor-pointer text-foreground"
                } ${index > 0 ? "mt-1" : ""}`}
                aria-disabled={isDisabled}
              >
                <div>
                  <Checkbox
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() => toggleColor(color.id)}
                  />
                </div>
                <div className="ml-[23.988px] flex min-w-0 flex-1 items-center gap-[10px]">
                  <span
                    className="inline-flex max-w-full items-center gap-[7px] rounded-full border py-[2px] pl-1.5 pr-2.5 text-sm leading-[18px]"
                    style={{
                      backgroundColor: color.hex,
                      borderColor: textColor,
                      color: textColor,
                    }}
                  >
                    <span
                      className="h-3 w-3 shrink-0 rounded-full border"
                      style={{
                        backgroundColor: color.hex,
                        borderColor: textColor,
                      }}
                    />
                    <span className="truncate">{color.label}</span>
                  </span>
                </div>
                {counts && (
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {count.toLocaleString()}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div
        className={`h-[0.882px] w-full border-b border-[rgba(0,0,0,0.1)] ${isExpanded ? "mt-[15px]" : ""}`}
      />
    </div>
  );
}
